/**
 * BasePipeline.ts
 *
 * Abstract base class for processing pipelines with configurable passes.
 * Provides common functionality for pass management, timing, and execution.
 *
 * Usage:
 * - TInput: Type of input data passed to process()
 * - TOutput: Type of output data returned from process()
 * - TContext: Type of internal context passed between passes (defaults to TOutput)
 * - TKwargs: Type of pass-specific configuration
 *
 * Supports two processor modes:
 * 1. Item processors: Process individual items (e.g., Polygon -> Polygon)
 * 2. Batch processors: Process all items at once (e.g., Polygon[] -> Polygon[])
 */

/**
 * Configuration for a single pass
 */
export interface PassConfig<TKwargs = Record<string, unknown>> {
  name: string;
  processorId: string;
  enabled: boolean;
  batchProcessor?: boolean;
  kwargs?: TKwargs;
}

/**
 * Complete pipeline configuration
 */
export interface PipelineConfig<TKwargs = Record<string, unknown>> {
  passes: PassConfig<TKwargs>[];
}

/**
 * Internal pass definition
 * TItem is the item type for conditions (for arrays, this is the element type)
 */
export interface ProcessingPass<TContext, TKwargs = Record<string, unknown>, TItem = TContext> {
  name: string;
  processor: ProcessorFunction<TContext, TKwargs>;
  processorId: string;
  kwargs?: TKwargs;
  batchProcessor?: boolean;
  condition?: ConditionFunction<TItem>;
  processesChildren?: boolean;
}

/**
 * Processor function type - can be synchronous or asynchronous
 * For batch processors, TContext should be an array type
 */
export type ProcessorFunction<TContext, TKwargs = Record<string, unknown>> =
  | ((context: TContext, kwargs?: TKwargs) => TContext | Promise<TContext>)
  | ((context: TContext, kwargs?: TKwargs, signal?: AbortSignal) => TContext | Promise<TContext>);

/**
 * Condition function to determine if a pass should run on an item
 * Only used for per-item processors (not batch processors)
 */
export type ConditionFunction<TItem> = (item: TItem, processAll: boolean) => boolean;

/**
 * Options for processing
 */
export interface ProcessOptions {
  signal?: AbortSignal;
}

/**
 * Processor registry entry
 */
export interface ProcessorRegistryEntry<TContext, TKwargs = Record<string, unknown>, TItem = TContext> {
  func: ProcessorFunction<TContext, TKwargs>;
  condition?: ConditionFunction<TItem>;
  batchProcessor?: boolean;
  processesChildren?: boolean;
  defaultKwargs?: TKwargs;
}

/**
 * Abstract base class for processing pipelines
 * TItem is for condition functions (element type for array contexts)
 */
export abstract class BasePipeline<TInput, TOutput, TContext = TOutput, TKwargs = Record<string, unknown>, TItem = TContext> {
  protected passes: ProcessingPass<TContext, TKwargs, TItem>[] = [];
  protected enabledPasses: Set<string> = new Set();
  public passTimings: Map<string, number> = new Map();

  // Processor registry for config-based loading (public for UI access)
  public processorRegistry: Map<string, ProcessorRegistryEntry<TContext, TKwargs, TItem>> = new Map();

  // Callback for when pipeline is loaded from config
  private onPipelineLoadedCallback?: () => void;

  // Track if pipeline was loaded from config file
  private pipelineLoadedFromFile = false;

  // Store the filename the pipeline was loaded from (for export)
  public loadedFromFilename: string | null = null;

  // Promise that resolves when pipeline is loaded (or rejects on failure)
  private pipelineLoadPromise: Promise<boolean> | null = null;
  private pipelineLoadResolve: ((loaded: boolean) => void) | null = null;

  /**
   * Convert input to internal context format
   * Override this to transform input data before processing
   */
  protected abstract inputToContext(input: TInput): TContext | Promise<TContext>;

  /**
   * Convert internal context to output format
   * Override this to transform context data after processing
   */
  protected abstract contextToOutput(context: TContext): TOutput | Promise<TOutput>;

  /**
   * Process data through the pipeline
   */
  async process(input: TInput, options?: ProcessOptions): Promise<TOutput> {
    let context = await this.inputToContext(input);
    this.passTimings.clear();

    for (const pass of this.passes) {
      // Check for abort signal
      if (options?.signal?.aborted) {
        throw new Error(`Pipeline aborted before pass: ${pass.name}`);
      }

      // Skip disabled passes
      if (!this.enabledPasses.has(pass.name)) {
        this.logPassSkipped(pass.name);
        continue;
      }

      const startTime = performance.now();
      this.logPassStart(pass.name);

      try {
        // Execute processor
        const result = pass.processor(context, pass.kwargs, options?.signal);
        const processedContext = result instanceof Promise ? await result : result;

        // Hook for post-processing after each pass
        context = await this.afterPass(processedContext, pass);
      } catch (e) {
        this.logPassFailed(pass.name, e);
        // Continue to next pass (or rethrow if needed)
      }

      const elapsedTime = performance.now() - startTime;
      this.passTimings.set(pass.name, elapsedTime);
      this.logPassComplete(pass.name, elapsedTime);
    }

    return this.contextToOutput(context);
  }

  /**
   * Hook called after each pass completes
   * Override to add custom post-processing logic
   */
  protected async afterPass(context: TContext, _pass: ProcessingPass<TContext, TKwargs, TItem>): Promise<TContext> {
    return context;
  }

  /**
   * Add a processing pass to the pipeline
   */
  addPass(
    name: string,
    processorFunc: ProcessorFunction<TContext, TKwargs>,
    options: {
      processorId?: string;
      kwargs?: TKwargs;
      batchProcessor?: boolean;
      condition?: ConditionFunction<TItem>;
      processesChildren?: boolean;
    } = {},
  ): this {
    this.passes.push({
      name,
      processor: processorFunc,
      processorId: options.processorId || name,
      kwargs: options.kwargs,
      batchProcessor: options.batchProcessor,
      condition: options.condition,
      processesChildren: options.processesChildren,
    });
    this.enabledPasses.add(name);
    return this;
  }

  /**
   * Get all pass names
   */
  getPassNames(): string[] {
    return this.passes.map((p) => p.name);
  }

  /**
   * Enable a pass by name
   */
  enablePass(name: string): void {
    this.enabledPasses.add(name);
  }

  /**
   * Disable a pass by name
   */
  disablePass(name: string): void {
    this.enabledPasses.delete(name);
  }

  /**
   * Check if a pass is enabled
   */
  isPassEnabled(name: string): boolean {
    return this.enabledPasses.has(name);
  }

  /**
   * Clear all passes from the pipeline
   */
  clear(): this {
    this.passes = [];
    this.enabledPasses.clear();
    return this;
  }

  /**
   * Get a pass by index
   */
  getPass(index: number): ProcessingPass<TContext, TKwargs, TItem> | undefined {
    return this.passes[index];
  }

  /**
   * Get a pass by name
   */
  getPassByName(name: string): ProcessingPass<TContext, TKwargs, TItem> | undefined {
    return this.passes.find((p) => p.name === name);
  }

  /**
   * Remove a pass by index
   */
  removePass(index: number): boolean {
    if (index >= 0 && index < this.passes.length) {
      const removed = this.passes.splice(index, 1)[0];
      this.enabledPasses.delete(removed.name);
      return true;
    }
    return false;
  }

  /**
   * Remove a pass by name
   */
  removePassByName(name: string): boolean {
    const index = this.passes.findIndex((p) => p.name === name);
    if (index >= 0) {
      return this.removePass(index);
    }
    return false;
  }

  /**
   * Move a pass from one index to another
   */
  movePass(fromIndex: number, toIndex: number): boolean {
    if (fromIndex < 0 || fromIndex >= this.passes.length || toIndex < 0 || toIndex >= this.passes.length || fromIndex === toIndex) {
      return false;
    }
    const [pass] = this.passes.splice(fromIndex, 1);
    this.passes.splice(toIndex, 0, pass);
    return true;
  }

  /**
   * Insert a pass at a specific index
   */
  insertPass(
    index: number,
    name: string,
    processorFunc: ProcessorFunction<TContext, TKwargs>,
    options: {
      processorId?: string;
      kwargs?: TKwargs;
      batchProcessor?: boolean;
      condition?: ConditionFunction<TItem>;
      processesChildren?: boolean;
    } = {},
  ): boolean {
    if (index < 0 || index > this.passes.length) {
      return false;
    }
    this.passes.splice(index, 0, {
      name,
      processor: processorFunc,
      processorId: options.processorId || name,
      kwargs: options.kwargs,
      batchProcessor: options.batchProcessor,
      condition: options.condition,
      processesChildren: options.processesChildren,
    });
    this.enabledPasses.add(name);
    return true;
  }

  /**
   * Update kwargs for a pass by name
   */
  updatePassKwargs(name: string, kwargs: TKwargs): boolean {
    const pass = this.passes.find((p) => p.name === name);
    if (pass) {
      pass.kwargs = { ...pass.kwargs, ...kwargs };
      return true;
    }
    return false;
  }

  /**
   * Export pipeline configuration
   */
  getConfig(): PipelineConfig<TKwargs> {
    return {
      passes: this.passes.map((pass) => ({
        name: pass.name,
        processorId: pass.processorId,
        enabled: this.enabledPasses.has(pass.name),
        batchProcessor: pass.batchProcessor,
        kwargs: pass.kwargs,
      })),
    };
  }

  /**
   * Export pipeline configuration to JSON
   */
  exportToJSON(): string {
    return JSON.stringify(this.getConfig(), null, 2);
  }

  // Logging methods (can be overridden for custom logging)

  protected logPassSkipped(_name: string): void {
    // Override in subclass for custom logging
  }

  protected logPassStart(_name: string): void {
    // Override in subclass for custom logging
  }

  protected logPassComplete(_name: string, _elapsedMs: number): void {
    // Override in subclass for custom logging
  }

  protected logPassFailed(_name: string, _error: unknown): void {
    // Override in subclass for custom logging
  }

  // Processor registry management

  /**
   * Register a processor function with an ID for config-based loading
   */
  registerProcessor(
    processorId: string,
    func: ProcessorFunction<TContext, TKwargs>,
    options: {
      condition?: ConditionFunction<TItem>;
      batchProcessor?: boolean;
      processesChildren?: boolean;
      defaultKwargs?: TKwargs;
    } = {},
  ): this {
    this.processorRegistry.set(processorId, {
      func,
      condition: options.condition,
      batchProcessor: options.batchProcessor,
      processesChildren: options.processesChildren,
      defaultKwargs: options.defaultKwargs,
    });
    return this;
  }

  /**
   * Get all registered processor IDs
   */
  getRegisteredProcessorIds(): string[] {
    return Array.from(this.processorRegistry.keys());
  }

  /**
   * Get default kwargs for a registered processor
   */
  getDefaultKwargsForProcessor(processorId: string): TKwargs | undefined {
    const processor = this.processorRegistry.get(processorId);
    return processor?.defaultKwargs;
  }

  /**
   * Add a pass from the processor registry by processorId
   * This is a convenience method for UI components
   */
  addPassFromRegistry(processorId: string, kwargs?: TKwargs): boolean {
    const processor = this.processorRegistry.get(processorId);
    if (!processor) {
      console.warn(`Unknown processor ID: ${processorId}`);
      return false;
    }

    // Generate a unique name based on processorId
    const baseName = processorId.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    const existingNames = new Set(this.passes.map((p) => p.name));
    let passName = baseName;
    let index = 1;

    while (existingNames.has(passName)) {
      passName = `${baseName} ${index}`;
      index++;
    }

    // Use provided kwargs or default kwargs
    const finalKwargs = kwargs || processor.defaultKwargs;

    // Add the pass
    this.addPass(passName, processor.func, {
      condition: processor.condition,
      batchProcessor: processor.batchProcessor,
      processesChildren: processor.processesChildren,
      kwargs: finalKwargs,
      processorId: processorId,
    });

    // Enable by default
    this.enablePass(passName);

    return true;
  }

  /**
   * Load pipeline configuration from a PipelineConfig object
   */
  loadFromConfig(config: PipelineConfig<TKwargs>): boolean {
    try {
      // Clear existing passes
      this.clear();

      for (const passConfig of config.passes) {
        const processor = this.processorRegistry.get(passConfig.processorId);
        if (!processor) {
          console.warn(`Unknown processor ID: ${passConfig.processorId}`);
          continue;
        }

        // Merge config kwargs with default kwargs (config takes precedence)
        const mergedKwargs = { ...processor.defaultKwargs, ...passConfig.kwargs } as TKwargs;

        this.addPass(passConfig.name, processor.func, {
          condition: processor.condition,
          batchProcessor: passConfig.batchProcessor !== undefined ? passConfig.batchProcessor : processor.batchProcessor,
          processesChildren: processor.processesChildren,
          kwargs: mergedKwargs,
          processorId: passConfig.processorId,
        });

        // Set enabled state
        if (!passConfig.enabled) {
          this.disablePass(passConfig.name);
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to load pipeline from config:', error);
      return false;
    }
  }

  /**
   * Load pipeline from JSON string
   */
  loadFromJSON(json: string): boolean {
    try {
      const config = JSON.parse(json) as PipelineConfig<TKwargs>;
      return this.loadFromConfig(config);
    } catch (error) {
      console.error('Failed to parse pipeline JSON:', error);
      return false;
    }
  }

  /**
   * Load pipeline configuration from external file
   */
  async loadFromFile(url: string): Promise<boolean> {
    // Create promise if not exists
    if (!this.pipelineLoadPromise) {
      this.pipelineLoadPromise = new Promise((resolve) => {
        this.pipelineLoadResolve = resolve;
      });
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load pipeline config from ${url}`);
      }
      const json = await response.text();
      const success = this.loadFromJSON(json);

      if (success) {
        this.pipelineLoadedFromFile = true;
        // Extract filename from URL path (e.g., '/config/segmentation/foo.json' -> 'foo.json')
        const pathParts = url.split('/');
        this.loadedFromFilename = pathParts[pathParts.length - 1] || null;
        if (this.onPipelineLoadedCallback) {
          this.onPipelineLoadedCallback();
        }
      }

      // Resolve the promise
      this.pipelineLoadResolve?.(success);
      return success;
    } catch (error) {
      console.warn(`Failed to load config file from ${url}:`, error);
      // Resolve with false on failure (not reject, since this is a valid outcome)
      this.pipelineLoadResolve?.(false);
      return false;
    }
  }

  /**
   * Wait for pipeline config to be loaded from file.
   * Returns true if loaded successfully, false otherwise.
   * If no file load was initiated, resolves immediately with false.
   */
  async waitForLoad(): Promise<boolean> {
    if (this.pipelineLoadedFromFile) {
      return true;
    }
    if (this.pipelineLoadPromise) {
      return this.pipelineLoadPromise;
    }
    // No load was initiated
    return false;
  }

  /**
   * Set callback to be called when pipeline is loaded from config file.
   * If the pipeline was already loaded, the callback is called immediately.
   */
  setOnPipelineLoaded(callback: () => void): this {
    this.onPipelineLoadedCallback = callback;
    // If already loaded, call immediately
    if (this.pipelineLoadedFromFile) {
      callback();
    }
    return this;
  }
}
